import {
    Table,
    TableBody,
    TableCell, TableHead, TableHeader,
    TableRow,
} from "../ui/table"

import {Card, CardContent, CardHeader} from "../ui/card"
import {Button} from "../ui/button";

import { Trash2, Pencil, UserPlus } from "lucide-react";
import {useState} from "react";
import ConfirmDialog from "../ConfirmDialog";
import UpdateUserDialog from "./UpdateUserDialog";
import CreateUserDialog from "./CreateUserDialog";
import IndividualLimitGuidance from "./IndividualLimitGuidance";

const UserStatus = ({ isActive }) => {
    const active = isActive ?? true;
    return (
        <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full shrink-0 ${active ? 'bg-yavrio-green' : 'bg-slate-400'}`} />
            <span className={`text-sm font-medium ${active ? 'text-emerald-700' : 'text-slate-500'}`}>
                {active ? 'Active' : 'Inactive'}
            </span>
        </div>
    );
};

export default function UsersTable({ users, setUsers, onDeleteUser, onUpdateUser, onCreateUser, isCustom = null }) {

    const [selectedRow, setSelectedRow] = useState(null);
    const [selectedUser, setSelectedUser] = useState({
        id: '',
        name: '',
        email: '',
        role: '',
        maxAuthValue: 0,
        isActive: true,
    });

    const handleSelectRow = (id) => {
        const selected = users.find(user => user.id === id);
        if (!selected) return;
        setSelectedRow(id);
        setSelectedUser({
            id: id,
            name: selected.name,
            email: selected.email,
            role: selected.role,
            maxAuthValue: selected.maxAuthValue ?? 0,
            isActive: selected.isActive ?? true,
        });
    };
    
    const handleConfirmDelete = async () => {
        const userDeleted = await onDeleteUser(selectedRow);

        if (userDeleted) {
            const updatedData = users.filter(user => user.id !== selectedRow);
            setUsers(updatedData);
        }
    };

    const handleConfirmUpdate = async (updates) => {
        const updatedUser = {
            ...selectedUser,
            ...updates,
        };

        await onUpdateUser(updatedUser);
    };

    // Sort users by Id before rendering the table
    const sortedUsers = [...(users ?? [])].sort((a, b) => a.id - b.id);

    return (
        <>
            <Card>
                <CardHeader>
                    <div className="flex flex-row items-center justify-between">
                        <h3 className="my-0">Users</h3>
                        <CreateUserDialog onSave={onCreateUser} isCustom={isCustom}>
                            <Button className="yvr-button gap-2">
                                <UserPlus className="w-4 h-4" /> New User
                            </Button>
                        </CreateUserDialog>
                    </div>
                </CardHeader>
                <CardContent>
                    {isCustom === null && (
                        <p role="alert" className="text-sm text-red-600 mb-4">
                            Unable to determine the organisation's approval mode. Please reload the page to try again.
                        </p>
                    )}
                    {isCustom && (
                        <IndividualLimitGuidance
                            isCustom={isCustom}
                            id="users-individual-limit-guidance"
                            className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-3"
                        />
                    )}
                    <Table>
                        <TableHeader>
                            <TableRow className='hover:bg-white'>
                                <TableHead className="w-[100px]">Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Role</TableHead>
                                <TableHead>
                                    {isCustom === null ? 'Individual limit' : isCustom ? 'Individual limit (standard approvals)' : 'Max Auth Value'}
                                </TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead></TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sortedUsers.map((user) => (
                                <TableRow key={user.userUUID} className='hover:bg-white'>
                                    <TableCell className="w-[200px]">{user.name}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.role}</TableCell>
                                    <TableCell>{(user.maxAuthValue ?? 0).toLocaleString()}</TableCell>
                                    <TableCell>
                                        <UserStatus isActive={user.isActive} />
                                    </TableCell>
                                    <TableCell className='text-right'>
                                        <UpdateUserDialog
                                            handleConfirmUpdate={handleConfirmUpdate}
                                            user={selectedUser}
                                            isCustom={isCustom}
                                        >
                                            <Button variant={'ghost'} className='p-2 text-yavrio-blue' onClick={() => handleSelectRow(user.id)}><Pencil className="h-4 w-4" /></Button>
                                        </UpdateUserDialog>
                                    </TableCell>
                                    <TableCell className='text-right'>
                                        <ConfirmDialog
                                            title="Confirm Delete"
                                            description={
                                                <>
                                                    {`If you proceed, `}
                                                    <b>{`${selectedUser.name} (${selectedUser.email})`}</b>
                                                    {` will no longer be able to approve payments. Are you sure you want to delete this user?`}
                                                </>
                                            }
                                            cancel='Cancel'
                                            action='Delete'
                                            variant="destructive"
                                            handleAction={handleConfirmDelete}
                                        >
                                            <Button variant={'ghost'} className='p-2 text-red-400' onClick={() => handleSelectRow(user.id)}><Trash2 className="h-4 w-4" /></Button>
                                        </ConfirmDialog>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </>
    )
}
